import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  ShieldAlert, Activity, Key, Ban, Mail, Monitor,
  RefreshCw, CheckCircle2, XCircle, AlertTriangle,
  Download, Trash2, LogOut, Eye, Copy, Clock,
  TrendingUp, Users, Lock, KeyRound, FileLock2, Bug
} from "lucide-react";

// ==================== HEADER COMPONENT ====================
const SecurityStats = ({ stats, onRefresh }) => {
  if (!stats) return null;
  const COLORS = {
    indigo: { border: "border-indigo-200", bg: "bg-gradient-to-br from-white to-indigo-50/40", text: "text-indigo-700", icon: "text-indigo-500" },
    emerald: { border: "border-emerald-200", bg: "bg-gradient-to-br from-white to-emerald-50/40", text: "text-emerald-700", icon: "text-emerald-500" },
    red: { border: "border-red-200", bg: "bg-gradient-to-br from-white to-red-50/40", text: "text-red-700", icon: "text-red-500" },
    violet: { border: "border-violet-200", bg: "bg-gradient-to-br from-white to-violet-50/40", text: "text-violet-700", icon: "text-violet-500" },
    amber: { border: "border-amber-200", bg: "bg-gradient-to-br from-white to-amber-50/40", text: "text-amber-700", icon: "text-amber-500" },
    rose: { border: "border-rose-200", bg: "bg-gradient-to-br from-white to-rose-50/40", text: "text-rose-700", icon: "text-rose-500" },
    blue: { border: "border-blue-200", bg: "bg-gradient-to-br from-white to-blue-50/40", text: "text-blue-700", icon: "text-blue-500" },
    teal: { border: "border-teal-200", bg: "bg-gradient-to-br from-white to-teal-50/40", text: "text-teal-700", icon: "text-teal-500" },
  };
  const cards = [
    { label: "Sesiones activas", value: stats.active_sessions, icon: Monitor, color: "indigo" },
    { label: "Logins (24h)", value: stats.logins_24h, icon: TrendingUp, color: "emerald" },
    { label: "Logins fallidos (24h)", value: stats.failed_logins_24h, icon: XCircle, color: "red" },
    { label: "Audit events total", value: stats.audit_log?.total || 0, icon: Activity, color: "violet" },
    { label: "API Keys activas", value: stats.api_keys?.active || 0, icon: Key, color: "amber" },
    { label: "IPs bloqueadas", value: stats.blocked_ips?.count || 0, icon: Ban, color: "rose" },
    { label: "Emails en cola", value: stats.email_queue?.total || 0, icon: Mail, color: "blue" },
    { label: `MFA adopción`, value: `${stats.mfa_adoption?.rate || 0}%`, icon: Lock, color: "teal",
      subtitle: `${stats.mfa_adoption?.enabled}/${stats.mfa_adoption?.total_users}` },
    { label: "CSP Violations (24h)", value: stats.csp_violations_24h || 0, icon: Bug, color: "rose" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3" data-testid="security-stats">
      {cards.map((c, i) => {
        const Icon = c.icon;
        const cl = COLORS[c.color] || COLORS.indigo;
        return (
          <Card key={i} className={`${cl.border} ${cl.bg}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-slate-500 font-medium">{c.label}</p>
                  <p className={`text-2xl font-bold ${cl.text} mt-1`}>{c.value}</p>
                  {c.subtitle && <p className="text-[10px] text-slate-400 mt-0.5">{c.subtitle}</p>}
                </div>
                <Icon className={`w-5 h-5 ${cl.icon}`} />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// ==================== AUDIT LOG TAB ====================
const AuditLogTab = () => {
  const { API, token } = useAuth();
  const h = { Authorization: `Bearer ${token}` };
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [filter, setFilter] = useState({ action: "", entity: "" });

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (filter.action) params.set("action", filter.action);
      if (filter.entity) params.set("entity", filter.entity);
      const res = await axios.get(`${API}/security/audit-log?${params}`, { headers: h });
      setEntries(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error cargando audit log");
    } finally { setLoading(false); }
  }, [filter.action, filter.entity]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  const handleVerify = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/security/audit-log/verify`, { headers: h });
      setVerifyResult(res.data);
      if (res.data.is_valid) toast.success(`Cadena íntegra (${res.data.total_entries} entradas)`);
      else toast.error(`${res.data.broken.length} entrada(s) corruptas detectadas`);
    } catch (err) {
      toast.error("Error verificando cadena");
    } finally { setLoading(false); }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/security/admin/audit-log/export?limit=5000`, { headers: h });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exportado ${res.data.total} entradas`);
    } catch (_) { toast.error("Error exportando"); }
  };

  return (
    <div className="space-y-4" data-testid="audit-log-tab">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <FileLock2 className="w-5 h-5 text-violet-600" /> Audit Log forense
                {verifyResult && (verifyResult.is_valid ? (
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Cadena íntegra
                  </Badge>
                ) : (
                  <Badge className="bg-red-100 text-red-700 border-red-200 border gap-1">
                    <AlertTriangle className="w-3 h-3" /> {verifyResult.broken.length} rotos
                  </Badge>
                ))}
              </CardTitle>
              <CardDescription className="text-xs">
                Hash SHA-256 encadenado. Cada entrada firma la anterior.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Acción..."
                value={filter.action}
                onChange={(e) => setFilter({ ...filter, action: e.target.value })}
                className="h-9 w-36 text-sm"
                data-testid="filter-action"
              />
              <Input
                placeholder="Entidad..."
                value={filter.entity}
                onChange={(e) => setFilter({ ...filter, entity: e.target.value })}
                className="h-9 w-36 text-sm"
                data-testid="filter-entity"
              />
              <Button size="sm" variant="outline" onClick={handleVerify} disabled={loading} data-testid="btn-verify-chain">
                <ShieldAlert className="w-4 h-4 mr-1" /> Verificar integridad
              </Button>
              <Button size="sm" variant="outline" onClick={handleExport} data-testid="btn-export-audit">
                <Download className="w-4 h-4 mr-1" /> Exportar
              </Button>
              <Button size="sm" variant="ghost" onClick={fetchLog}>
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto border rounded-lg max-h-[520px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 border-b">
                <tr className="text-slate-600">
                  <th className="text-left py-2 px-3 font-medium">#</th>
                  <th className="text-left py-2 px-3 font-medium">Fecha</th>
                  <th className="text-left py-2 px-3 font-medium">Usuario</th>
                  <th className="text-left py-2 px-3 font-medium">Acción</th>
                  <th className="text-left py-2 px-3 font-medium">Entidad</th>
                  <th className="text-left py-2 px-3 font-medium">IP</th>
                  <th className="text-left py-2 px-3 font-medium">Hash</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const broken = verifyResult?.broken?.find((b) => b.sequence === e.sequence);
                  return (
                    <tr
                      key={e.id}
                      className={`border-b hover:bg-slate-50 ${broken ? "bg-red-50" : ""}`}
                      data-testid={`audit-row-${e.sequence}`}
                    >
                      <td className="py-2 px-3 font-mono text-slate-500">{e.sequence}</td>
                      <td className="py-2 px-3 text-slate-700">
                        {new Date(e.timestamp).toLocaleString("es-VE")}
                      </td>
                      <td className="py-2 px-3">
                        <div className="text-slate-800">{e.user_email || "-"}</div>
                        <div className="text-slate-400 text-[10px]">{e.user_role}</div>
                      </td>
                      <td className="py-2 px-3">
                        <Badge variant="outline" className="font-mono text-[10px]">
                          {e.action}
                        </Badge>
                      </td>
                      <td className="py-2 px-3 text-slate-600">{e.entity}</td>
                      <td className="py-2 px-3 font-mono text-slate-500">{e.ip}</td>
                      <td className="py-2 px-3">
                        <code className={`text-[10px] font-mono ${broken ? "text-red-600" : "text-slate-400"}`}>
                          {(e.hash || "").slice(0, 10)}...
                        </code>
                        {broken && <AlertTriangle className="w-3 h-3 text-red-600 inline ml-1" />}
                      </td>
                    </tr>
                  );
                })}
                {entries.length === 0 && !loading && (
                  <tr><td colSpan={7} className="text-center py-8 text-slate-400">Sin entradas</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== API KEYS TAB ====================
const ApiKeysTab = () => {
  const { API, token } = useAuth();
  const h = { Authorization: `Bearer ${token}` };
  const [keys, setKeys] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [form, setForm] = useState({ name: "", scopes: [], environment: "live", expires_days: 365 });
  const [loading, setLoading] = useState(false);

  const fetchAll = async () => {
    try {
      const [ks, sc] = await Promise.all([
        axios.get(`${API}/security/api-keys`, { headers: h }),
        axios.get(`${API}/security/api-keys/scopes`, { headers: h }),
      ]);
      setKeys(ks.data || []);
      // Backend retorna dict {scope_name: description}. Normalizamos a array de {name, description}.
      const data = sc.data;
      if (Array.isArray(data)) {
        setScopes(data.map((s) => ({ name: s, description: s })));
      } else if (data && typeof data === "object") {
        setScopes(Object.entries(data).map(([name, description]) => ({ name, description: String(description) })));
      } else {
        setScopes([]);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error cargando API Keys");
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleCreate = async () => {
    if (!form.name.trim()) return toast.error("Nombre requerido");
    if (form.scopes.length === 0) return toast.error("Seleccione al menos un scope");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/security/api-keys`, form, { headers: h });
      setNewKey(res.data);
      setShowCreate(false);
      setForm({ name: "", scopes: [], environment: "live", expires_days: 365 });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error creando key");
    } finally { setLoading(false); }
  };

  const handleRevoke = async (id, name) => {
    if (!window.confirm(`¿Revocar API Key "${name}"? Esta acción es irreversible.`)) return;
    try {
      await axios.put(`${API}/security/api-keys/${id}/revoke`, {}, { headers: h });
      toast.success("Key revocada");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const toggleScope = (s) => {
    setForm((f) => ({
      ...f,
      scopes: f.scopes.includes(s) ? f.scopes.filter((x) => x !== s) : [...f.scopes, s],
    }));
  };

  return (
    <div className="space-y-4" data-testid="api-keys-tab">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-amber-600" /> API Keys
              </CardTitle>
              <CardDescription className="text-xs">Llaves con scopes para integraciones externas</CardDescription>
            </div>
            <Button size="sm" onClick={() => setShowCreate(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-create-key">
              <Key className="w-4 h-4 mr-1" /> Nueva API Key
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {newKey && (
            <div className="mb-4 p-4 bg-emerald-50 border border-emerald-300 rounded-lg" data-testid="new-key-banner">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-emerald-900">API Key creada: {newKey.name}</p>
                  <p className="text-xs text-emerald-700 mt-1">⚠️ Guarde esta clave ahora. No se mostrará de nuevo.</p>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="flex-1 font-mono text-xs bg-white p-2 rounded border break-all">{newKey.api_key}</code>
                    <Button size="sm" variant="outline" onClick={() => {
                      navigator.clipboard.writeText(newKey.api_key);
                      toast.success("Copiada al portapapeles");
                    }}>
                      <Copy className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setNewKey(null)}>Cerrar</Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showCreate && (
            <div className="mb-4 p-4 border rounded-lg space-y-3" data-testid="create-key-form">
              <Input
                placeholder="Nombre descriptivo (ej: Integración Catastro Externo)"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="input-key-name"
              />
              <div>
                <p className="text-xs font-medium text-slate-600 mb-2">Scopes:</p>
                <div className="flex flex-wrap gap-2">
                  {scopes.map((s) => (
                    <button
                      key={s.name}
                      type="button"
                      title={s.description}
                      onClick={() => toggleScope(s.name)}
                      className={`text-xs px-3 py-1 rounded-full border ${
                        form.scopes.includes(s.name)
                          ? "bg-blue-100 border-blue-300 text-blue-700"
                          : "bg-white border-slate-200 text-slate-500"
                      }`}
                      data-testid={`scope-${s.name}`}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <select
                  value={form.environment}
                  onChange={(e) => setForm({ ...form, environment: e.target.value })}
                  className="h-9 text-sm border rounded px-2"
                >
                  <option value="live">live</option>
                  <option value="test">test</option>
                </select>
                <Input
                  type="number"
                  value={form.expires_days}
                  onChange={(e) => setForm({ ...form, expires_days: parseInt(e.target.value) || 365 })}
                  className="h-9 w-28 text-sm"
                />
                <span className="text-xs text-slate-500">días de vida</span>
                <div className="flex-1" />
                <Button size="sm" onClick={handleCreate} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-submit-create-key">
                  {loading && <RefreshCw className="w-3 h-3 mr-1 animate-spin" />} Crear
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowCreate(false)}>Cancelar</Button>
              </div>
            </div>
          )}

          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-slate-600 text-xs">
                  <th className="text-left py-2 px-3">Nombre</th>
                  <th className="text-left py-2 px-3">Prefijo</th>
                  <th className="text-left py-2 px-3">Scopes</th>
                  <th className="text-left py-2 px-3">Env</th>
                  <th className="text-left py-2 px-3">Uso</th>
                  <th className="text-left py-2 px-3">Estado</th>
                  <th className="text-left py-2 px-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id} className="border-b hover:bg-slate-50" data-testid={`key-row-${k.id}`}>
                    <td className="py-2 px-3 font-medium">{k.name}</td>
                    <td className="py-2 px-3 font-mono text-xs text-slate-500">{k.key_prefix}...</td>
                    <td className="py-2 px-3 text-xs">
                      <span className="text-slate-400">{(k.scopes || []).length} scopes</span>
                    </td>
                    <td className="py-2 px-3">
                      <Badge variant="outline" className="text-[10px]">{k.environment}</Badge>
                    </td>
                    <td className="py-2 px-3 text-xs text-slate-500">{k.usage_count || 0}</td>
                    <td className="py-2 px-3">
                      {k.is_active ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border">Activa</Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-500">Revocada</Badge>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      {k.is_active && (
                        <Button size="sm" variant="ghost" onClick={() => handleRevoke(k.id, k.name)} className="text-red-600 h-7" data-testid={`btn-revoke-${k.id}`}>
                          <Ban className="w-3 h-3 mr-1" /> Revocar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {keys.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-6 text-slate-400">Sin API Keys</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== BLOCKED IPS TAB ====================
const BlockedIpsTab = () => {
  const { API, token } = useAuth();
  const h = { Authorization: `Bearer ${token}` };
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchBlocked = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/security/blocked-logins`, { headers: h });
      setBlocked(res.data || []);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchBlocked(); }, []);

  const handleUnblock = async (identifier) => {
    try {
      await axios.post(`${API}/security/unblock-login/${encodeURIComponent(identifier)}`, {}, { headers: h });
      toast.success("Desbloqueado");
      fetchBlocked();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <Card data-testid="blocked-ips-tab">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Ban className="w-5 h-5 text-rose-600" /> IPs / Identificadores Bloqueados
            </CardTitle>
            <CardDescription className="text-xs">Bloqueos por protección de fuerza bruta (5/30min, 10/2h, 20/permanente)</CardDescription>
          </div>
          <Button size="sm" variant="ghost" onClick={fetchBlocked}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {blocked.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-300 mb-2" />
            <p className="text-sm">No hay bloqueos activos</p>
          </div>
        ) : (
          <div className="space-y-2">
            {blocked.map((b, i) => (
              <div key={i} className="flex items-center justify-between p-3 border rounded-lg bg-rose-50/40" data-testid={`blocked-${i}`}>
                <div>
                  <p className="font-mono text-sm font-medium">{b.identifier || b.ip}</p>
                  <p className="text-xs text-slate-500">
                    Intentos: {b.attempts || b.failed_count || 0}
                    {b.blocked_until && ` · hasta ${new Date(b.blocked_until).toLocaleString("es-VE")}`}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => handleUnblock(b.identifier || b.ip)} className="border-emerald-300 text-emerald-700" data-testid={`btn-unblock-${i}`}>
                  Desbloquear
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== EMAIL QUEUE TAB ====================
const EmailQueueTab = () => {
  const { API, token } = useAuth();
  const h = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState({ mode: "stub", items: [] });
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const fetchQ = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/security/admin/email-queue?limit=100`, { headers: h });
      setData(res.data || { mode: "stub", items: [] });
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchQ(); }, []);

  const handleClear = async () => {
    if (!window.confirm("¿Borrar TODOS los emails registrados? Útil en stub mode para limpiar.")) return;
    try {
      await axios.delete(`${API}/security/admin/email-queue`, { headers: h });
      toast.success("Cola limpiada");
      fetchQ();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <Card data-testid="email-queue-tab">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" /> Cola de Emails
              <Badge className={data.mode === "live"
                ? "bg-emerald-100 text-emerald-700 border-emerald-200 border"
                : "bg-amber-100 text-amber-700 border-amber-200 border"}>
                Modo: {data.mode.toUpperCase()}
              </Badge>
            </CardTitle>
            <CardDescription className="text-xs">
              {data.mode === "stub"
                ? "RESEND_API_KEY no configurado - emails registrados pero no enviados"
                : "Emails enviados vía Resend"}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={fetchQ}>
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            {data.items.length > 0 && (
              <Button size="sm" variant="outline" onClick={handleClear} className="border-red-300 text-red-700" data-testid="btn-clear-queue">
                <Trash2 className="w-4 h-4 mr-1" /> Limpiar cola
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {data.items.length === 0 ? (
          <div className="text-center py-8 text-slate-400">Cola vacía</div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b">
                <tr className="text-slate-600">
                  <th className="text-left py-2 px-3">Fecha</th>
                  <th className="text-left py-2 px-3">Para</th>
                  <th className="text-left py-2 px-3">Asunto</th>
                  <th className="text-left py-2 px-3">Categoría</th>
                  <th className="text-left py-2 px-3">Estado</th>
                  <th className="text-left py-2 px-3">Preview</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((e, i) => (
                  <tr key={i} className="border-b hover:bg-slate-50" data-testid={`email-row-${i}`}>
                    <td className="py-2 px-3 text-slate-600">{new Date(e.created_at).toLocaleString("es-VE")}</td>
                    <td className="py-2 px-3 font-mono">{e.to}</td>
                    <td className="py-2 px-3">{e.subject}</td>
                    <td className="py-2 px-3"><Badge variant="outline" className="text-[10px]">{e.category}</Badge></td>
                    <td className="py-2 px-3">
                      <Badge className={
                        e.status === "sent" ? "bg-emerald-100 text-emerald-700 border-emerald-200 border" :
                        e.status === "error" ? "bg-red-100 text-red-700 border-red-200 border" :
                        "bg-amber-100 text-amber-700 border-amber-200 border"
                      }>
                        {e.status}
                      </Badge>
                    </td>
                    <td className="py-2 px-3">
                      {e.html_preview && (
                        <Button size="sm" variant="ghost" onClick={() => setPreview(e)} className="h-6 text-xs">
                          <Eye className="w-3 h-3" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {preview && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setPreview(null)}>
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold">{preview.subject}</h3>
                  <p className="text-xs text-slate-500">Para: {preview.to}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setPreview(null)}>Cerrar</Button>
              </div>
              <div className="border rounded p-4" dangerouslySetInnerHTML={{ __html: preview.html_preview }} />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== SESSIONS TAB ====================
const SessionsTab = () => {
  const { API, token, user } = useAuth();
  const h = { Authorization: `Bearer ${token}` };
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchS = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/security/admin/sessions?limit=200`, { headers: h });
      setSessions(res.data || []);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchS(); }, []);

  const handleRevoke = async (jti, email) => {
    if (!window.confirm(`¿Cerrar la sesión de ${email}?`)) return;
    try {
      await axios.delete(`${API}/security/admin/sessions/${jti}`, { headers: h });
      toast.success("Sesión cerrada");
      fetchS();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <Card data-testid="sessions-system-tab">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" /> Sesiones activas del sistema
              <Badge variant="outline">{sessions.length}</Badge>
            </CardTitle>
            <CardDescription className="text-xs">Todas las sesiones abiertas de todos los usuarios</CardDescription>
          </div>
          <Button size="sm" variant="ghost" onClick={fetchS}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b">
              <tr className="text-slate-600">
                <th className="text-left py-2 px-3">Usuario</th>
                <th className="text-left py-2 px-3">Rol</th>
                <th className="text-left py-2 px-3">IP</th>
                <th className="text-left py-2 px-3">Inicio</th>
                <th className="text-left py-2 px-3">Expira</th>
                <th className="text-left py-2 px-3">Dispositivo</th>
                <th className="text-left py-2 px-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s, i) => {
                const isMe = s.user_id === user?.id;
                return (
                  <tr key={i} className={`border-b hover:bg-slate-50 ${isMe ? "bg-blue-50/30" : ""}`} data-testid={`session-sys-${i}`}>
                    <td className="py-2 px-3">
                      <div className="font-medium">{s.user_email || "-"}</div>
                      <div className="text-slate-400 text-[10px]">{s.user_name}</div>
                    </td>
                    <td className="py-2 px-3">
                      <Badge variant="outline" className="text-[10px]">{s.user_role}</Badge>
                    </td>
                    <td className="py-2 px-3 font-mono">{s.ip || "-"}</td>
                    <td className="py-2 px-3 text-slate-600">{new Date(s.created_at).toLocaleString("es-VE")}</td>
                    <td className="py-2 px-3 text-slate-500">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {new Date(s.expires_at).toLocaleDateString("es-VE")}
                    </td>
                    <td className="py-2 px-3 text-slate-600 truncate max-w-[160px]">{(s.user_agent || "").slice(0, 40)}</td>
                    <td className="py-2 px-3">
                      {s.refresh_jti && !isMe && (
                        <Button size="sm" variant="ghost" className="text-red-600 h-7" onClick={() => handleRevoke(s.refresh_jti, s.user_email)} data-testid={`btn-admin-revoke-${i}`}>
                          <LogOut className="w-3 h-3 mr-1" /> Cerrar
                        </Button>
                      )}
                      {isMe && <span className="text-[10px] text-blue-600 italic">(tu sesión)</span>}
                    </td>
                  </tr>
                );
              })}
              {sessions.length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-slate-400">Sin sesiones activas</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== CSP VIOLATIONS TAB ====================
const CspViolationsTab = () => {
  const { API, token } = useAuth();
  const h = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState({ items: [], stats: {}, total: 0 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchV = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/security/admin/csp-violations?limit=200`, { headers: h });
      setData(res.data || { items: [], stats: {}, total: 0 });
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchV(); }, []);

  const handleClear = async () => {
    if (!window.confirm("¿Eliminar TODAS las violaciones CSP registradas?")) return;
    try {
      await axios.delete(`${API}/security/admin/csp-violations`, { headers: h });
      toast.success("Limpiado");
      fetchV();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const directives = Object.entries(data.stats || {}).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-4" data-testid="csp-violations-tab">
      {directives.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Bug className="w-5 h-5 text-rose-600" /> Directivas más violadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {directives.slice(0, 10).map(([d, count]) => (
                <Badge key={d} className="bg-rose-100 text-rose-700 border-rose-200 border text-xs">
                  {d}: <span className="font-bold ml-1">{count}</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Bug className="w-5 h-5 text-rose-600" /> Violaciones CSP
                <Badge variant="outline">{data.total}</Badge>
              </CardTitle>
              <CardDescription className="text-xs">
                Reportes del navegador cuando bloquea un recurso. Útil para detectar intentos de XSS.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={fetchV}>
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
              {data.items.length > 0 && (
                <Button size="sm" variant="outline" onClick={handleClear} className="border-red-300 text-red-700" data-testid="btn-clear-csp">
                  <Trash2 className="w-4 h-4 mr-1" /> Limpiar
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {data.items.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-300 mb-2" />
              <p className="text-sm">Sin violaciones CSP registradas</p>
              <p className="text-xs text-slate-400 mt-1">Si el CSP bloquea un recurso legítimo o un ataque XSS, aparecerá aquí.</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden max-h-[480px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b sticky top-0">
                  <tr className="text-slate-600">
                    <th className="text-left py-2 px-3">Fecha</th>
                    <th className="text-left py-2 px-3">Directiva</th>
                    <th className="text-left py-2 px-3">Recurso bloqueado</th>
                    <th className="text-left py-2 px-3">Página</th>
                    <th className="text-left py-2 px-3">IP</th>
                    <th className="text-left py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((v) => (
                    <tr key={v.id} className="border-b hover:bg-slate-50" data-testid={`csp-row-${v.id}`}>
                      <td className="py-2 px-3 text-slate-600">{new Date(v.timestamp).toLocaleString("es-VE")}</td>
                      <td className="py-2 px-3">
                        <Badge className="bg-rose-100 text-rose-700 border-rose-200 border text-[10px] font-mono">
                          {v.violated_directive || "-"}
                        </Badge>
                      </td>
                      <td className="py-2 px-3 font-mono truncate max-w-[200px]" title={v.blocked_uri}>{v.blocked_uri || "-"}</td>
                      <td className="py-2 px-3 truncate max-w-[180px]" title={v.document_uri}>{v.document_uri || "-"}</td>
                      <td className="py-2 px-3 font-mono">{v.ip}</td>
                      <td className="py-2 px-3">
                        <Button size="sm" variant="ghost" onClick={() => setSelected(v)} className="h-6" data-testid={`btn-view-csp-${v.id}`}>
                          <Eye className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {selected && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setSelected(null)}>
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">Detalle de violación</h3>
                  <Button size="sm" variant="ghost" onClick={() => setSelected(null)}>Cerrar</Button>
                </div>
                <dl className="space-y-2 text-sm">
                  <div><dt className="text-slate-500 text-xs">Directiva violada</dt><dd className="font-mono">{selected.violated_directive}</dd></div>
                  <div><dt className="text-slate-500 text-xs">Recurso bloqueado</dt><dd className="font-mono break-all">{selected.blocked_uri}</dd></div>
                  <div><dt className="text-slate-500 text-xs">Página</dt><dd className="break-all">{selected.document_uri}</dd></div>
                  <div><dt className="text-slate-500 text-xs">Archivo fuente</dt><dd className="font-mono break-all">{selected.source_file} {selected.line_number ? `:${selected.line_number}:${selected.column_number || 0}` : ""}</dd></div>
                  {selected.sample && <div><dt className="text-slate-500 text-xs">Muestra</dt><dd><pre className="bg-slate-100 p-2 rounded text-xs overflow-auto">{selected.sample}</pre></dd></div>}
                  <div><dt className="text-slate-500 text-xs">IP / User-Agent</dt><dd className="font-mono text-xs">{selected.ip} · {selected.user_agent}</dd></div>
                </dl>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== MAIN PAGE ====================
const PanelAdminSeguridad = () => {
  const { API, token, user } = useAuth();
  const h = { Authorization: `Bearer ${token}` };
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("audit");

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/security/admin/stats`, { headers: h });
      setStats(res.data);
    } catch (_) { /* silencioso */ }
  }, []);

  useEffect(() => {
    fetchStats();
    const int = setInterval(fetchStats, 30000);  // auto-refresh 30s
    return () => clearInterval(int);
  }, [fetchStats]);

  if (user?.role !== "admin" && user?.role !== "funcionario") {
    return (
      <div className="p-8 text-center text-red-600">
        <ShieldAlert className="w-12 h-12 mx-auto mb-2" />
        <p>Acceso restringido. Solo administradores.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="panel-admin-seguridad">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
          <ShieldAlert className="w-6 h-6 text-red-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Panel de Administración de Seguridad</h1>
          <p className="text-slate-500 text-sm">
            Control centralizado del stack de seguridad del sistema SIRET
          </p>
        </div>
      </div>

      <SecurityStats stats={stats} onRefresh={fetchStats} />

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid grid-cols-6 w-full max-w-4xl" data-testid="tabs-security">
          <TabsTrigger value="audit" data-testid="tab-audit"><FileLock2 className="w-4 h-4 mr-1" /> Audit</TabsTrigger>
          <TabsTrigger value="keys" data-testid="tab-keys"><KeyRound className="w-4 h-4 mr-1" /> API Keys</TabsTrigger>
          <TabsTrigger value="blocked" data-testid="tab-blocked"><Ban className="w-4 h-4 mr-1" /> Bloqueos</TabsTrigger>
          <TabsTrigger value="emails" data-testid="tab-emails"><Mail className="w-4 h-4 mr-1" /> Emails</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="tab-sessions"><Users className="w-4 h-4 mr-1" /> Sesiones</TabsTrigger>
          <TabsTrigger value="csp" data-testid="tab-csp"><Bug className="w-4 h-4 mr-1" /> CSP</TabsTrigger>
        </TabsList>

        <TabsContent value="audit" className="mt-4"><AuditLogTab /></TabsContent>
        <TabsContent value="keys" className="mt-4"><ApiKeysTab /></TabsContent>
        <TabsContent value="blocked" className="mt-4"><BlockedIpsTab /></TabsContent>
        <TabsContent value="emails" className="mt-4"><EmailQueueTab /></TabsContent>
        <TabsContent value="sessions" className="mt-4"><SessionsTab /></TabsContent>
        <TabsContent value="csp" className="mt-4"><CspViolationsTab /></TabsContent>
      </Tabs>
    </div>
  );
};

export default PanelAdminSeguridad;
